import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Component, computed, effect, inject, PLATFORM_ID } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { catchError, forkJoin, map, of } from 'rxjs';
import type { HomePageData, SuccessStoriesPageData } from '../../../shared/models/marketing.models';
import { MarketingFooterComponent } from '../../components/marketing-footer.component';
import { MarketingHeaderComponent } from '../../components/marketing-header.component';
import { MarketingConsultationCtaComponent } from '../../components/marketing-consultation-cta.component';
import { SuccessStoriesGridComponent } from '../../components/success-stories/success-stories-grid.component';
import { SuccessStoriesHeroComponent } from '../../components/success-stories/success-stories-hero.component';
import {
  heroStatsFromHome,
  mapSuccessStoryCards,
} from '../../components/success-stories/success-stories.utils';
import { PagesService } from '../../../shared/services/pages.service';
import { buildPageTitle } from '../../../shared/constants/site-meta';

export type SuccessStoriesLoadState =
  | { kind: 'pending' }
  | { kind: 'error' }
  | {
      kind: 'ok';
      page: SuccessStoriesPageData;
      homeStats: Array<{ value: string; label: string }>;
      homeCta?: HomePageData['cta'];
      homeDestinations: string[];
    };

@Component({
  selector: 'app-marketing-success-stories-page',
  standalone: true,
  imports: [
    RouterLink,
    MarketingHeaderComponent,
    MarketingFooterComponent,
    SuccessStoriesHeroComponent,
    SuccessStoriesGridComponent,
    MarketingConsultationCtaComponent,
  ],
  templateUrl: './success-stories-page.component.html',
})
export class MarketingSuccessStoriesPageComponent {
  private readonly pages = inject(PagesService);
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly route = inject(ActivatedRoute);

  readonly highlightSlug = toSignal(this.route.fragment.pipe(map((f) => f ?? '')), {
    initialValue: '',
  });

  readonly pageState = toSignal(
    forkJoin({
      page: this.pages.getMarketingSuccessStories(),
      home: this.pages.getMarketingHome().pipe(catchError(() => of(null))),
    }).pipe(
      map(({ page, home }) => ({
        kind: 'ok' as const,
        page,
        homeStats: home?.stats ?? [],
        homeCta: home?.cta,
        homeDestinations: home?.destinations?.countries?.map((c) => c.name) ?? [],
      })),
      catchError(() =>
        isPlatformServer(this.platformId)
          ? of({ kind: 'pending' as const })
          : of({ kind: 'error' as const }),
      ),
    ),
    { initialValue: { kind: 'pending' as const } satisfies SuccessStoriesLoadState },
  );

  readonly allStories = computed(() => {
    const state = this.pageState();
    if (state.kind !== 'ok') return [];
    return mapSuccessStoryCards(state.page.items);
  });

  readonly hasAnyStories = computed(() => this.allStories().length > 0);

  readonly heroStats = computed(() => {
    const state = this.pageState();
    if (state.kind !== 'ok') return heroStatsFromHome();
    return heroStatsFromHome(state.homeStats);
  });

  constructor() {
    effect(() => {
      const state = this.pageState();
      if (state.kind === 'ok') {
        this.applySeo(state.page);
      }
    });

    effect(() => {
      if (!isPlatformBrowser(this.platformId)) return;
      const slug = this.highlightSlug();
      const state = this.pageState();
      if (!slug || state.kind !== 'ok') return;
      queueMicrotask(() =>
        document.getElementById(slug)?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      );
    });
  }

  private applySeo(page: SuccessStoriesPageData): void {
    const seo = page.seo;
    const documentTitle = buildPageTitle(seo.title);
    this.title.setTitle(documentTitle);
    if (seo.description) this.meta.updateTag({ name: 'description', content: seo.description });
  }
}
